// General
import React, { useContext, useState, useEffect } from "react";
// UI Components
import { Modal } from "react-bootstrap";
import { TextField, MenuItem, Autocomplete } from "@mui/material";
// Formik
import { Formik, Form, Field } from "formik";
// Redux
import { useDispatch, useSelector } from "react-redux";
// Contexts
import { PersonelContext } from "../../data/personel/PersonelContext";
import { getCities, getDistricts } from "../../data/general/LocationSlice";
import CapitalizedFormikField from "../../components/CapitalizedFormikField";
import { useValidations } from "../../data/ValidationContext";

// PersonelAddModal bileşeni
function PersonelAddModal({ show, handleClose }) {
  const dispatch = useDispatch(); // Redux dispatch
  const { addPersonel } = useContext(PersonelContext); // Personel ekleme fonksiyonu
  const { vs_personel } = useValidations(); // Formik validation şeması
  const { cities, districts } = useSelector((state) => state.location); // Redux state'ten şehir/ilçe verileri

  const [searchedCity, setSearchedCity] = useState(""); // Daha önce seçilen şehir

  // Formik başlangıç değerleri
  const initialValues = {
    fullName: "",
    email: "",
    password: "",
    position: "",
    company: "",
    phone: "",
    prim: "",
    city: "",
    district: "",
    address: "",
    status: "Aktif",
    workType: "Tam Zamanlı",
  };

  // Modal açıldığında şehir listesini çek
  useEffect(() => {
    if (show && cities.length === 0) {
      dispatch(getCities({ signal: null }));
    }
  }, [show, cities.length, dispatch]);

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="custom-modal-width">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={vs_personel}
        onSubmit={(values) => {
          addPersonel(values); // Personeli ekle
          handleClose(); // Modalı kapat
        }}
      >
        {({ values, touched, errors, setFieldValue, setFieldTouched, handleChange }) => (
          <Form>
            {/* Modal Header */}
            <Modal.Header closeButton>
              <Modal.Title>Personel Ekle</Modal.Title>
            </Modal.Header>

            {/* Modal Body */}
            <Modal.Body>
              <div className="d-flex flex-column gap-3">

                {/* Ad Soyad */}
                <CapitalizedFormikField
                  name="fullName"
                  label="Ad Soyad"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={touched.fullName && !!errors.fullName}
                  helperText={touched.fullName && errors.fullName}
                />

                {/* E-posta ve Şifre */}
                <div className="d-flex gap-2">
                  <CapitalizedFormikField
                    name="email"
                    label="E-Posta"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                  <CapitalizedFormikField
                    type="password"
                    name="password"
                    label="Şifre"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                  />
                </div>

                {/* Pozisyon */}
                <CapitalizedFormikField
                  name="position"
                  label="Pozisyon"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={touched.position && !!errors.position}
                  helperText={touched.position && errors.position}
                />

                {/* Bayiler */}
                <Field
                  as={TextField}
                  name="company"
                  label="Bayiler"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={touched.company && !!errors.company}
                  helperText={touched.company && errors.company}
                />

                {/* Telefon ve Prim */}
                <div className="d-flex gap-2">
                  <CapitalizedFormikField
                    name="phone"
                    label="Telefon"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.phone && !!errors.phone}
                    helperText={touched.phone && errors.phone}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, ""); // rakam dışı karakterleri temizle
                    }}
                  />
                  <CapitalizedFormikField
                    name="prim"
                    label="Prim"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.prim && !!errors.prim}
                    helperText={touched.prim && errors.prim}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, ""); // rakam dışı karakterleri temizle
                    }}
                  />
                </div>

                {/* Şehir & İlçe Seçimi */}
                <div className="d-flex gap-2">
                  {/* Şehir */}
                  <div style={{ flex: 1 }}>
                    <Autocomplete
                      options={cities}
                      value={values.city || null}
                      onChange={(e, newValue) => {
                        setFieldValue("city", newValue || "");
                        setFieldValue("district", ""); // şehir değişirse ilçe sıfırla
                        if (newValue && newValue !== searchedCity) {
                          dispatch(getDistricts({ signal: null, city: newValue })); // ilçe verilerini çek
                          setSearchedCity(newValue);
                        }
                      }}
                      onBlur={() => setFieldTouched("city", true)}
                      getOptionLabel={(option) => option?.name || option || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Şehir"
                          size="small"
                          variant="outlined"
                          fullWidth
                          error={touched.city && !!errors.city}
                          helperText={touched.city && errors.city}
                        />
                      )}
                    />
                  </div>

                  {/* İlçe */}
                  <div style={{ flex: 1 }}>
                    <Autocomplete
                      options={districts || []}
                      value={values.district || null}
                      onChange={(e, newValue) => setFieldValue("district", newValue || "")}
                      onBlur={() => setFieldTouched("district", true)}
                      getOptionLabel={(option) => option?.name || option || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="İlçe"
                          size="small"
                          variant="outlined"
                          fullWidth
                          error={touched.district && !!errors.district}
                          helperText={touched.district && errors.district}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Adres */}
                <CapitalizedFormikField
                  name="address"
                  label="Adres"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  error={touched.address && !!errors.address}
                  helperText={touched.address && errors.address}
                />

                {/* Mesai Durumu */}
                <CapitalizedFormikField
                  select
                  name="workType"
                  label="Mesai Durumu"
                  value={values.workType}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="Tam Zamanlı">Tam Zamanlı</MenuItem>
                  <MenuItem value="Vardiya">Vardiya</MenuItem>
                  <MenuItem value="Part-Time">Part-Time</MenuItem>
                </CapitalizedFormikField>

              </div>
            </Modal.Body>

            {/* Modal Footer */}
            <Modal.Footer>
              <button type="button" className="btn btn-secondary" onClick={handleClose}>
                İptal
              </button>
              <button type="submit" className="btn btn-primary">
                Kaydet
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default PersonelAddModal;
